/**
 * Lønnskjøring as three steps: Kontroller, Godkjenn timer, Eksporter (designer's report, C11).
 *
 * The page had all of this already, flat: a list headed "det regnskapsføreren kommer til å
 * spørre om", a table, and two download buttons in the header. The report's point is that a
 * pay run is a sequence, and the export is its last step rather than the page's shape.
 *
 * What counts as a blocker is read off the file the export actually writes
 * (backend/controllers/admin/shiftController.js generateTripletexCSV, lines 1910-1957), not
 * guessed:
 *
 *   - Ansattnr is empty when the person has no employee_id, so the import refuses the row. It
 *     used to be our row id, which Tripletex refused or booked on whoever had that number.
 *   - Lønnsartnummer is written as an empty string when the line has no salary_type_code
 *     (:1932, :1940). A line with no pay code is a line the import cannot place.
 *   - Only lines with hours or a rate are written (:1935), so a check counts those lines and
 *     not every pay type that exists.
 *
 * Nothing here blocks the download. A manager who knows the file is imperfect and wants it
 * anyway gets it; what changes is that they are told exactly which rows will be wrong, with
 * the count, before they click.
 */
import type {
  ApiAbsenceType,
  ApiCompanyBank,
  ApiPayrollType,
  ApiRateRule,
  ApiUser,
  ReportRow,
} from '@/api/data';
import { T } from '@/i18n';
import { weekMissing } from '../ansatt/roster';
import { overtimeBelowLaw } from '../innstillinger/overtime';
import type { PayLine } from './lines';

export type Severity = 'blocker' | 'note';
export type StepKey = 'kontroller' | 'godkjenn' | 'eksporter';

export interface Check {
  key: string;
  step: StepKey;
  /** One line, with the count in it. */
  title: string;
  /** What it does to the file or to the money, one sentence. */
  detail: string;
  count: number;
  severity: Severity;
  to?: string;
  /** Each person, day or line the check counts, with where it is fixed (#264): an aggregate never drops who. */
  records?: CheckRecord[];
}

export interface CheckRecord {
  key: string;
  /** the person, or the pay type for a line */
  who: string;
  /** YYYY-MM-DD when the record is a day */
  date?: string;
  /** what is wrong with this one, when the title does not say it */
  what?: string;
  to?: string;
}

const nameOfUser = (u: { name?: string | null; lastname?: string | null }) =>
  `${u.name ?? ''} ${u.lastname ?? ''}`.trim();
/** the employee's card, opened on the field that is missing (Ansatt.tsx ?rediger=) */
const card = (id: number | string, field?: string) => `/ansatte/${id}${field ? `?rediger=${field}` : ''}`;
/** one registration in Timer, opened beside the list, on the clock field to put in when one is named */
const shiftAt = (uid: number | string, id: number, field?: 'ut' | 'begge') =>
  `/timeliste?ansatt=${uid}&vis=alle&vakt=${id}${field ? `&rett=${field}` : ''}`;
const peopleRecords = (
  list: { id: number | string; name?: string | null; lastname?: string | null }[],
  field?: string,
): CheckRecord[] => list.map((u) => ({ key: `u${u.id}`, who: nameOfUser(u), to: card(u.id, field) }));

export interface RunInput {
  /** Active employees, and anyone who left with pay in the period; the page already filters. */
  users: ApiUser[];
  lines: PayLine[];
  rows: ReportRow[];
  absenceTypes: ApiAbsenceType[];
  /** The company's pay types and rules, for the 40 % overtime floor; absent until loaded. */
  payTypes?: ApiPayrollType[];
  rateRules?: ApiRateRule[];
  /** Innstillinger → Prosjekter «Prosjekt påkrevd» (#115): approved hours without a project are listed */
  projectsRequired?: boolean;
  /** Each person with a timebank, and the company's rule for unused hours (plan 0027 G2 slice 3). */
  banks?: ApiCompanyBank[];
  /** The period's shifts, for a person whose end date has passed and who still has shifts after it (#108). */
  shifts?: {
    id?: number;
    user_id: number | null;
    start_time: string;
    hours?: number | null;
    approved?: boolean;
  }[];
  /** Today, YYYY-MM-DD in Oslo; the end-date check reads it. */
  today?: string;
  unused?: { rule: 'pay' | 'carry' | null | undefined; day: string | null | undefined };
  period?: { from: string; to: string };
  /** Innstillinger → Lønn «Lønnssystem» (#110): which file the export makes */
  payrollSystem?: PayrollSystem;
}

export type PayrollSystem = 'tripletex' | 'poweroffice' | 'visma';

/** One line per row of the Tripletex file, as the generator decides which rows exist. */
export interface ExportRow {
  userId: string;
  payType: string;
  code: string;
}
export function exportRows(rows: ReportRow[], system?: string): ExportRow[] {
  const out: ExportRow[] = [];
  for (const r of rows)
    for (const [payType, line] of Object.entries(r.totals ?? {})) {
      if (payType === 'TOTAL Salary') continue;
      if (!((line.hours ?? 0) > 0 || (line.rate ?? 0) > 0)) continue;
      // the PowerOffice file leaves out a line with no code that carries no pay (an unpaid absence at
      // rate 0, NAV's days: services/payExportPowerOffice.js, #183), so it is no blocker there
      const l = line as {
        rate?: number | string | null;
        paid_by?: string;
        salary_type_code?: string | number | null;
      };
      if (
        system === 'poweroffice' &&
        !String(l.salary_type_code ?? '').trim() &&
        (l.paid_by === 'nav' || (l.rate != null && !(Number(l.rate) > 0)))
      )
        continue;
      out.push({ userId: r.id, payType, code: line.salary_type_code ?? '' });
    }
  return out;
}

/** The reasons the report gives on an absence line (backend sickDays, ammefriHours, the avspasering check), in words, with their source. */
export const LINE_NOTES: Record<string, { title: string; detail: string; lines?: [string, string] }> = {
  under_4_weeks: {
    title: 'ansatt under fire uker',
    detail:
      'Arbeidsgiver betaler sykepenger først etter fire ukers ansettelse (folketrygdloven § 8-18). Dagene står på Nav-linjen; Nav betaler fra første dag.',
  },
  start_unknown: {
    title: 'mangler Ansatt fra',
    detail:
      'Første vakt Timefly kjenner er under fire uker før sykdommen. Fyll inn Ansatt fra på ansattkortet, så avgjøres fireukersregelen.',
  },
  sick_age_70: {
    title: 'fylt 70 år',
    detail:
      'Ingen sykepenger, heller ikke i arbeidsgiverperioden, fra og med fylte 70 år (folketrygdloven § 8-3). Linjen står med null dager.',
  },
  sick_age_67_under_2g: {
    title: 'over 67 år, under 2G',
    detail:
      'Mellom 67 og 70 år gjelder retten til sykepenger bare når inntektsgrunnlaget er over 2G (folketrygdloven § 8-51). Grunnlaget her er lavere, så ingen dager betales.',
  },
  sick_age_67_nav_60: {
    title: 'over 67 år',
    detail:
      'Arbeidsgiverperioden er som ellers, men Nav betaler høyst 60 sykepengedager mellom 67 og 70 år (folketrygdloven § 8-51). Timefly teller ikke de 60 dagene; følg dem opp i lønnssystemet.',
  },
  no_plan: {
    title: 'ingen vakter i planen',
    detail:
      'Ingen dager er betalt. Uten plan betaler arbeidsgiver bare hvis personen har jobbet minst tre måneder uten opphold over 14 dager (Navs regel for tilkallingsvikarer).',
  },
  nav_on_call: {
    title: 'regnet etter Navs regel for tilkallingsvikarer',
    detail:
      'Ingen vakter i planen: dagene er vakter de siste tre månedene × 7,5 ÷ 3 ÷ 21,67, som Nav beskriver.',
  },
  worked_same_day: {
    title: 'jobbet og syk samme dag',
    detail:
      'En planlagt sykedag med godkjente arbeidstimer gir ikke sykepenger den dagen (Nav: delvis dag). Er det gradert sykmelding, legg inn sykmeldingsgraden på fraværet, så betales den andelen.',
  },
  ammefri_child_age: {
    title: 'ammefri',
    detail:
      'Ammefri betales med inntil én time på dager med sju timer eller mer, bare i barnets første leveår (arbeidsmiljøloven § 12-8). Timefly kjenner ikke barnets alder: sjekk den.',
  },
  to_timebank: {
    title: 'overtid til timebanken',
    detail:
      'Med skriftlig avtale om avspasering går overtidstimene, eller den avtalte andelen, i timebanken. Overtidstillegget betales nå; selve timene betales når de tas ut som avspasering (arbeidsmiljøloven § 10-6 (12)).',
    lines: ['overtidslinje', 'overtidslinjer'],
  },
  // plan 0027 batch F: the hours past the chapter 11 ceilings are paid with the supplement and named
  youth_overtime: {
    title: 'overtid for ansatt under 18 år',
    detail:
      'Arbeidstakere under 18 år har ikke lov til å jobbe overtid (arbeidsmiljøloven § 11-2). Timene over 8 timer om dagen og 40 i uken, 7 og 35 under 15 år og for en 15-åring som fortsatt går i grunnskolen eller ikke er besvart på ansattkortet, er betalt med overtidstillegg, men arbeidet var et brudd på loven. Endre planen så det ikke skjer igjen.',
    lines: ['overtidslinje', 'overtidslinjer'],
  },
  // #234: overtime the law asks for where the company has no overtime rule, and a fixed-kr rate under 40 %
  statutory_overtime: {
    title: 'overtid etter loven, uten overtidsregel',
    detail:
      'Bedriften har ingen overtidsregel, men arbeid over 9 timer om dagen eller 40 i uken er overtid med minst 40 % tillegg (arbeidsmiljøloven § 10-6). Linjen er regnet med 40 % og har ingen lønnsart. Legg inn en overtidsregel under Innstillinger, Lønn, så erstatter den denne linjen.',
    lines: ['overtidslinje', 'overtidslinjer'],
  },
  overtime_below_floor: {
    title: 'overtidstillegg under 40 %',
    detail:
      'Overtidstillegget er et fast kronebeløp som er under 40 % av timelønnen til denne personen (arbeidsmiljøloven § 10-6 (11)). Linjen er betalt som satt; sjekk satsen.',
    lines: ['overtidslinje', 'overtidslinjer'],
  },
  avspasering_with_overtime: {
    title: 'avspasering og betalt overtid i samme periode',
    detail:
      'Overtid som tas ut som avspasering skal ikke betales to ganger (arbeidsmiljøloven § 10-6). Sjekk at avspaseringen ikke gjelder timer som også er betalt som overtid.',
  },
  hoytid_start_unknown: {
    title: '1. eller 17. mai uten Ansatt fra',
    detail:
      'Full lønn for 1. og 17. mai gjelder den som har vært ansatt sammenhengende de siste 30 dagene (lov om 1. og 17. mai § 3). Fyll inn Ansatt fra på ansattkortet, så regnes det ut.',
  },
  hoytid_under_30_days: {
    title: 'ansatt under 30 dager før 1. eller 17. mai',
    detail:
      'Retten til full lønn for 1. og 17. mai krever 30 dagers sammenhengende ansettelse (lov om 1. og 17. mai § 3). Linjen er tatt med; sjekk om den skal betales.',
  },
  hoytid_on_ferie: {
    title: '1. eller 17. mai i ferien',
    detail:
      'Den som har ferie 1. eller 17. mai skal ha lønn for dagen (forskrift til lov om 1. og 17. mai, IV), og kan kreve ferien forlenget med en dag. Uten vakter den uken er dagen regnet som avtalt uke delt på fem.',
  },
  over_6g: {
    title: 'dagsats over 6G',
    detail:
      'Lovens plikt stopper ved 6G i året (folketrygdloven § 8-10). Satsen er ikke kuttet; betal over 6G bare hvis avtalen sier det.',
  },
};

export function checks(input: RunInput): Check[] {
  const { users, lines, rows, absenceTypes, payTypes = [], rateRules = [] } = input;
  const out: Check[] = [];

  // AML §10-6(11): "Tillegget skal være minst 40 prosent". New saves under it are refused since
  // 2026-09-23 (G1); one saved before that is named here and paid as saved, the run not blocked.
  // only real lists: an answer of another shape (an error, a stub) says nothing rather than breaking the page
  const types = Array.isArray(payTypes) ? payTypes : [];
  const rules = Array.isArray(rateRules) ? rateRules : [];
  const low = types.filter((p) =>
    overtimeBelowLaw(
      p,
      rules.filter((r) => r.payroll_type_id === p.id),
    ),
  );
  if (low.length)
    out.push({
      key: 'overtime-below-law',
      step: 'kontroller',
      title: T('{n, plural, one {# overtidssats er under 40 %} other {# overtidssatser er under 40 %}}', {
        n: low.length,
      }),
      detail: T(
        'Arbeidsmiljøloven § 10-6 (11) krever minst 40 prosent tillegg. {names} betales slik den er lagret til satsen rettes.',
        { names: low.map((p) => p.name).join(', ') },
      ),
      count: low.length,
      severity: 'note',
      to: '/innstillinger/lonn',
      records: low.map((p) => ({ key: `t${p.id}`, who: p.name, to: '/innstillinger/lonn' })),
    });
  const sum = (f: (l: PayLine) => number) => lines.reduce((a, l) => a + f(l), 0);
  const dueOf = (kind: 'unapproved' | 'open' | 'unpunched', field?: 'ut' | 'begge'): CheckRecord[] =>
    lines.flatMap((l) =>
      (l.due ?? [])
        .filter((x) => x.kind === kind)
        .map((x) => ({
          key: `s${x.id}`,
          who: nameOfUser(l.u),
          date: x.date,
          to: shiftAt(l.u.id, x.id, field),
        })),
    );

  const open = sum((l) => l.open);
  if (open)
    out.push({
      key: 'open',
      step: 'godkjenn',
      title: T('{n, plural, one {# åpen stempling} other {# åpne stemplinger}}', { n: open }),
      detail: 'Dagen kan ikke godkjennes før den er lukket, så timene blir ikke med.',
      count: open,
      severity: 'blocker',
      to: '/timeliste',
      records: dueOf('open', 'ut'),
    });

  const unapproved = sum((l) => l.unapproved);
  if (unapproved)
    out.push({
      key: 'unapproved',
      step: 'godkjenn',
      title: T('{n, plural, one {# stemplet dag} other {# stemplede dager}} er ikke godkjent', {
        n: unapproved,
      }),
      detail: 'Bare godkjente dager står i filen. Disse timene blir ikke betalt.',
      count: unapproved,
      severity: 'blocker',
      to: '/timeliste',
      records: dueOf('unapproved'),
    });

  // The file's Ansattnr column: employee_id, else empty.
  const noEmp = users.filter((u) => !u.employee_id);
  const paid = new Set(exportRows(rows).map((r) => String(r.userId)));
  const noEmpPaid = noEmp.filter((u) => paid.has(String(u.id)));
  if (noEmpPaid.length)
    out.push({
      key: 'employee-number',
      step: 'kontroller',
      title: T('{n, plural, one {# ansatt} other {# ansatte}} med lønn i perioden mangler ansattnummer', {
        n: noEmpPaid.length,
      }),
      detail:
        'Ansattnr står tomt for dem i filen, så lønnssystemet avviser linjene. Fyll inn ansattnummeret fra lønnssystemet på ansattkortet.',
      count: noEmpPaid.length,
      severity: 'blocker',
      to: '/ansatte?mangler=1',
      records: peopleRecords(noEmpPaid, 'employee_id'),
    });

  // Two people under one number (#168): the payroll system cannot tell them apart, so the server
  // refuses the file and the send. The number each report row carries is the one the file writes
  // (Lønns-ID, else Ansattnr); PowerOffice reads digits as a number, so 77 and 000077 are one.
  const keyOf = (v: string | null | undefined) => {
    const t = String(v ?? '').trim();
    if (!t) return null;
    return input.payrollSystem === 'poweroffice' && /^\d+$/.test(t) ? t.replace(/^0+(?=\d)/, '') : t;
  };
  const byNumber = new Map<string, ReportRow[]>();
  for (const r of rows) {
    const k = keyOf(r.employee_id);
    if (!k || !paid.has(String(r.id))) continue;
    const list = byNumber.get(k) ?? [];
    if (!list.some((x) => x.id === r.id)) list.push(r);
    byNumber.set(k, list);
  }
  const sharedNumbers = [...byNumber.values()].filter((g) => g.length > 1);
  if (sharedNumbers.length) {
    const people = sharedNumbers.reduce((a, g) => a + g.length, 0);
    out.push({
      key: 'employee-number-shared',
      step: 'kontroller',
      title: T('{n, plural, one {# ansatt} other {# ansatte}} har samme ansattnummer som en annen', {
        n: people,
      }),
      detail: T(
        '{groups}. Lønnssystemet kan ikke skille dem, så filen lages ikke. Gi hver sitt nummer på ansattkortet.',
        {
          groups: sharedNumbers
            .map(
              (g) =>
                `${g.map((r) => r.name).join(` ${T('og')} `)} (${String(g[0]?.employee_id ?? '').trim()})`,
            )
            .join('; '),
        },
      ),
      count: people,
      severity: 'blocker',
      to: '/ansatte',
      records: sharedNumbers.flat().map((r) => ({
        key: `u${r.id}`,
        who: r.name,
        what: T('ansattnr {id}', { id: String(r.employee_id ?? '').trim() }),
        to: card(r.id, 'employee_id'),
      })),
    });
  }

  // «Prosjekt påkrevd» (#115): the file's Prosjektnummer is empty for these hours, so the accountant
  // cannot book them on a project; the file still goes, the hours are named
  if (input.projectsRequired) {
    const noProject = rows.filter((r) => (r.hours_without_project ?? 0) > 0);
    const sumNo = noProject.reduce((a, r) => a + (r.hours_without_project ?? 0), 0);
    if (noProject.length)
      out.push({
        key: 'no-project',
        step: 'kontroller',
        title: T('{n, plural, one {# ansatt} other {# ansatte}} har godkjente timer uten prosjekt', {
          n: noProject.length,
        }),
        detail: T(
          '{hours} timer står uten prosjektnummer i filen: {names}. Sett prosjekt på vaktene under Timer.',
          {
            hours: Math.round(sumNo * 100) / 100,
            names: `${noProject
              .map((r) => r.name)
              .slice(0, 4)
              .join(', ')}${noProject.length > 4 ? ' …' : ''}`,
          },
        ),
        count: noProject.length,
        severity: 'note',
        to: '/timeliste?prosjekt=uten&vis=godkjent',
        records: noProject.map((r) => ({
          key: `u${r.id}`,
          who: r.name,
          what: T('{n} t', { n: Math.round((r.hours_without_project ?? 0) * 100) / 100 }),
          to: `/timeliste?ansatt=${r.id}&prosjekt=uten&vis=godkjent`,
        })),
      });
  }

  // An hourly person with hours on the base line and no hourly rate (review 2026-10-06): the line is
  // 0 kr on this screen and goes to the file with an empty Sats, so the month total is short
  const noRate = rows.filter((r) => {
    const base = r.totals?.Regular;
    const u = users.find((x) => String(x.id) === String(r.id));
    return base && (base.hours ?? 0) > 0 && !(Number(base.rate) > 0) && u?.contract_type !== 'monthly';
  });
  if (noRate.length)
    out.push({
      key: 'hourly-rate',
      step: 'kontroller',
      title: T('{n, plural, one {# ansatt} other {# ansatte}} med timer mangler timelønn', {
        n: noRate.length,
      }),
      detail: T(
        'Timene står med 0 kr her og uten sats i filen: {names}. Legg inn timelønnen på ansattkortet.',
        {
          names: noRate
            .map((r) => r.name)
            .slice(0, 4)
            .join(', '),
        },
      ),
      count: noRate.length,
      severity: 'blocker',
      to: '/ansatte',
      records: noRate.map((r) => ({ key: `u${r.id}`, who: r.name, to: card(r.id, 'hourly_rate') })),
    });

  // Plan 0027 batch A (owner, 2026-09-24: warn, never block): without a birth date the age rules,
  // the under-18 limits and the sick pay ages, were not checked for this person.
  const noBirth = users.filter((u) => u.birth_date_missing && paid.has(String(u.id)));
  if (noBirth.length)
    out.push({
      key: 'birth-date-missing',
      step: 'kontroller',
      title: T('{n, plural, one {# ansatt} other {# ansatte}} med lønn i perioden mangler fødselsdato', {
        n: noBirth.length,
      }),
      detail:
        'Reglene for arbeidstakere under 18 år og sykepenger etter 67 og 70 år er ikke sjekket for dem. Fyll inn fødselsdato på ansattkortet.',
      count: noBirth.length,
      severity: 'note',
      to: '/ansatte?mangler=1',
      records: peopleRecords(noBirth, 'birth_date'),
    });

  // #54: no positive agreed week. The weekly limit, and the hourly base of a salaried person, are
  // counted with the company's week, else 37,5 (backend services/agreedWeek.js); said, never blocked
  const noWeek = users.filter((u) => weekMissing(u) && paid.has(String(u.id)));
  if (noWeek.length)
    out.push({
      key: 'agreed-week-missing',
      step: 'kontroller',
      title: T(
        '{n, plural, one {# ansatt} other {# ansatte}} med lønn i perioden mangler avtalt arbeidstid per uke',
        { n: noWeek.length },
      ),
      detail:
        'Ukegrensen og timesatsen for fastlønte regnes da med bedriftens uke, eller 37,5 timer når bedriften ikke har satt den. Sett avtalt arbeidstid på ansattkortet.',
      count: noWeek.length,
      severity: 'note',
      to: '/ansatte?oppsett=uke',
      records: peopleRecords(noWeek, 'agreed_week_hours'),
    });

  // The file's Lønnsartnummer column, counted on the rows the file will hold. A pay code is
  // edited in two different places: an absence type's under Innstillinger → Vakt- og
  // fraværstyper, a work pay type's under Innstillinger → Lønn. The link follows whichever
  // the offending lines actually are, so it does not open a page with none of them on it.
  const noCode = exportRows(rows, input.payrollSystem).filter((r) => !r.code);
  const absenceNames = new Set(absenceTypes.map((t) => t.name));
  if (noCode.length)
    out.push({
      key: 'salary-code',
      step: 'kontroller',
      title: T('{n, plural, one {# linje får} other {# linjer får}} tomt lønnsartnummer', {
        n: noCode.length,
      }),
      detail: T('Uten lønnsart vet ikke lønnssystemet hva linjen er: {names}.', {
        names: [...new Set(noCode.map((r) => r.payType))].slice(0, 4).join(', '),
      }),
      count: noCode.length,
      severity: 'blocker',
      to: noCode.every((r) => absenceNames.has(r.payType)) ? '/innstillinger/typer' : '/innstillinger/lonn',
      records: [...new Set(noCode.map((r) => r.payType))].map((pt) => ({
        key: `p${pt}`,
        who: pt,
        what: T('{n, plural, one {# linje} other {# linjer}}', {
          n: noCode.filter((r) => r.payType === pt).length,
        }),
        to: absenceNames.has(pt) ? '/innstillinger/typer' : '/innstillinger/lonn',
      })),
    });

  // PowerOffice Go's FILE is Huldt & Lillevik fixed length and numeric (#110,
  // services/payExportPowerOffice.js; PowerOffice's example file is digits and spaces only):
  // ansattnr at most 6 digits, lønnsart at most 5. The server refuses a file with any other value,
  // so these block the file as the empty ones do; empty ones are counted above, not again here.
  // Send til PowerOffice is not held to this: Go's pay item codes are text, and the demo client
  // has codes like 120-et (live, 2026-10-14), which the send matches as they are.
  if (input.payrollSystem === 'poweroffice') {
    const badEmp = users.filter(
      (u) => u.employee_id && paid.has(String(u.id)) && !/^\d{1,6}$/.test(String(u.employee_id).trim()),
    );
    const badCode = exportRows(rows).filter((r) => r.code && !/^\d{1,5}$/.test(String(r.code).trim()));
    if (badEmp.length || badCode.length)
      out.push({
        key: 'poweroffice-fit',
        step: 'kontroller',
        title: T('{n, plural, one {# verdi passer} other {# verdier passer}} ikke i PowerOffice-filen', {
          n: badEmp.length + badCode.length,
        }),
        detail: T(
          'PowerOffice-filen har bare tall: ansattnummer med høyst 6 siffer og lønnsart med høyst 5. Send til PowerOffice tar også lønnsarter med bokstaver, som 120-et. {values}.',
          {
            values: [
              ...badEmp.map((u) => T('ansattnr {id}', { id: String(u.employee_id) })),
              ...new Set(badCode.map((r) => T('lønnsart {code}', { code: String(r.code) }))),
            ]
              .slice(0, 4)
              .join(', '),
          },
        ),
        count: badEmp.length + badCode.length,
        severity: 'blocker',
        to: badEmp.length ? '/ansatte' : '/innstillinger/lonn',
        records: [
          ...badEmp.map((u) => ({
            key: `u${u.id}`,
            who: nameOfUser(u),
            what: T('ansattnr {id}', { id: String(u.employee_id) }),
            to: card(u.id, 'employee_id'),
          })),
          ...[...new Set(badCode.map((r) => String(r.code)))].map((code) => ({
            key: `c${code}`,
            who: T('lønnsart {code}', { code }),
            to: '/innstillinger/lonn',
          })),
        ],
      });
  }

  // An absence type nobody has used this period still exports without a code the moment
  // somebody does, so it is worth saying, and it is not wrong today.
  const typesNoCode = absenceTypes.filter((t) => t.is_active && !t.salary_type_code);
  if (typesNoCode.length)
    out.push({
      key: 'absence-type-code',
      step: 'kontroller',
      title: T('{n, plural, one {# aktiv fraværstype} other {# aktive fraværstyper}} uten lønnsart', {
        n: typesNoCode.length,
      }),
      detail: T('Fravær på dem havner i filen uten lønnsartnummer: {names}.', {
        names: typesNoCode
          .map((t) => t.name)
          .slice(0, 4)
          .join(', '),
      }),
      count: typesNoCode.length,
      severity: 'note',
      to: '/innstillinger/typer',
      records: typesNoCode.map((t) => ({ key: `a${t.id}`, who: t.name, to: '/innstillinger/typer' })),
    });

  // The timebank (plan 0027 G2 slice 3). Someone who has left with hours in the bank must have
  // them paid (the owner, 2026-09-23): a blocker until Utbetal on their card has emptied it. A minus
  // balance is only said: pay may be docked for it only under a written agreement (aml § 14-15 (2)).
  const banks = Array.isArray(input.banks) ? input.banks : [];
  const leftOwed = banks.filter((b) => !b.active && b.balance > 0);
  if (leftOwed.length)
    out.push({
      key: 'timebank-leavers',
      step: 'kontroller',
      title: T(
        '{n, plural, one {# person som har sluttet, har} other {# personer som har sluttet, har}} timer i timebanken',
        {
          n: leftOwed.length,
        },
      ),
      detail: T('Timene skal utbetales: {names}. Bruk Utbetal under Timebank på ansattkortet.', {
        names: leftOwed
          .map((b) => `${b.name} ${b.lastname ?? ''}`.trim())
          .slice(0, 4)
          .join(', '),
      }),
      count: leftOwed.length,
      severity: 'blocker',
      to: '/ansatte',
      records: peopleRecords(leftOwed.map((b) => ({ ...b, id: b.user_id }))),
    });
  const leftMinus = banks.filter((b) => !b.active && b.balance < 0);
  if (leftMinus.length)
    out.push({
      key: 'timebank-leavers-minus',
      step: 'kontroller',
      title: T(
        '{n, plural, one {# person som har sluttet, har} other {# personer som har sluttet, har}} minus i timebanken',
        {
          n: leftMinus.length,
        },
      ),
      detail:
        'Timefly trekker ikke minustimer. Trekk i sluttoppgjøret krever skriftlig avtale (arbeidsmiljøloven § 14-15 (2)).',
      count: leftMinus.length,
      severity: 'note',
      to: '/ansatte',
      records: peopleRecords(leftMinus.map((b) => ({ ...b, id: b.user_id }))),
    });
  // the company pays unused hours on a date: remind in the period that holds it
  const day = input.unused?.rule === 'pay' ? input.unused.day : null;
  const period = input.period;
  if (day && period) {
    const hit = [period.from.slice(0, 4), period.to.slice(0, 4)]
      .map((y) => `${y}-${day}`)
      .find((d) => d >= period.from && d <= period.to);
    const owed = banks.filter((b) => b.active && b.balance > 0);
    if (hit && owed.length)
      out.push({
        key: 'timebank-payout-day',
        step: 'kontroller',
        title: T(
          '{n, plural, one {# person har} other {# personer har}} ubrukt avspasering som skal utbetales {date}',
          {
            n: owed.length,
            date: `${Number(hit.slice(8, 10))}.${Number(hit.slice(5, 7))}`,
          },
        ),
        detail:
          'Bedriften utbetaler ubrukte timer på denne datoen (Innstillinger → Lønn). Bruk Utbetal under Timebank på ansattkortet.',
        count: owed.length,
        severity: 'note',
        to: '/ansatte',
        records: peopleRecords(owed.map((b) => ({ ...b, id: b.user_id }))),
      });
  }

  // A temporary job's end date has passed and the person still has shifts after it (#108): the
  // contract ended, or it was extended and the date not moved. Said, never blocked: the hours were
  // worked and are paid either way.
  const todayIso = input.today;
  if (todayIso) {
    const after = users.filter((u) => {
      const end = u.end_date?.slice(0, 10);
      if (!end || end >= todayIso) return false;
      return (input.shifts ?? []).some((s) => s.user_id === u.id && (s.start_time.slice(0, 10) || '') > end);
    });
    if (after.length)
      out.push({
        key: 'end-date-passed',
        step: 'kontroller',
        title: T(
          '{n, plural, one {# person med passert sluttdato har} other {# personer med passert sluttdato har}} vakter etter den',
          {
            n: after.length,
          },
        ),
        detail: T(
          'Sluttdatoen i arbeidsavtalen er passert: {names}. Er ansettelsen forlenget, flytt sluttdatoen på ansattkortet; er den avsluttet, sett personen inaktiv.',
          {
            names: after
              .map((u) => `${u.name} ${u.lastname ?? ''}`.trim())
              .slice(0, 4)
              .join(', '),
          },
        ),
        count: after.length,
        severity: 'note',
        to: '/ansatte',
        records: peopleRecords(after, 'end_date'),
      });
  }

  // A banked overtime line with no rate (a salaried person with no salary set): the file's empty
  // Sats would let the payroll system price the banked hours at the overtime rate, and pay them
  // again when taken (review of 2026-09-24, plan 0027 G2)
  const unpriced = lines.reduce((a, l) => a + (l.sickNotes?.banked_unpriced ?? 0), 0);
  if (unpriced)
    out.push({
      key: 'banked-unpriced',
      step: 'kontroller',
      title: T(
        '{n, plural, one {# overtidslinje til timebanken mangler} other {# overtidslinjer til timebanken mangler}} sats',
        { n: unpriced },
      ),
      detail:
        'Personen har fastlønn uten månedslønn, så overtidstillegget kan ikke regnes ut. Legg inn månedslønnen på ansattkortet.',
      count: unpriced,
      severity: 'blocker',
      to: '/ansatte',
      records: peopleRecords(
        lines.filter((l) => l.sickNotes?.banked_unpriced).map((l) => l.u),
        'monthly_rate',
      ),
    });

  // why an absence line has what it has, one note per reason (D18, ammefri, avspasering; 2026-09-22)
  for (const [code, text] of Object.entries(LINE_NOTES)) {
    const count = lines.reduce((a, l) => a + (l.sickNotes?.[code] ?? 0), 0);
    if (count)
      out.push({
        key: `sick-${code}`,
        step: 'kontroller',
        title: text.lines
          ? T('{n, plural, one {# overtidslinje} other {# overtidslinjer}}: {why}', {
              n: count,
              why: T(text.title),
            })
          : T('{n, plural, one {# fraværslinje} other {# fraværslinjer}}: {why}', {
              n: count,
              why: T(text.title),
            }),
        detail: text.detail,
        count,
        severity: 'note',
        // «mangler Ansatt fra» opens that field on the card; the other reasons the person's card
        records: peopleRecords(
          lines.filter((l) => l.sickNotes?.[code]).map((l) => l.u),
          code === 'start_unknown' ? 'employment_start' : undefined,
        ),
      });
  }

  // D18 (2026-09-22): the dagsats comes from the three months before the sickness in Timefly
  const shortBasis = sum((l) => l.shortBasis);
  if (shortBasis)
    out.push({
      key: 'sick-basis',
      step: 'kontroller',
      title: T(
        '{n, plural, one {# fraværslinje} other {# fraværslinjer}} med dagsats fra under tre måneder',
        { n: shortBasis },
      ),
      detail:
        'Sykedager betales med dagsats fra de tre siste kalendermånedene. Timefly har mindre enn tre måneder med arbeid for personen, og uten arbeid står satsen tom. Sjekk satsen i lønnssystemet.',
      count: shortBasis,
      severity: 'note',
      records: peopleRecords(lines.filter((l) => l.shortBasis).map((l) => l.u)),
    });

  // D19 A (OWNER-DECISIONS-2026-09-22): a paid absence and approved work on the same day are both
  // paid; the report names the days (worked_dates on the absence line) and the manager decides
  const both = rows.flatMap((r) =>
    Object.values(r.totals ?? {}).flatMap((l) =>
      (l.worked_dates ?? []).map((d) => `${r.name} ${d.slice(8, 10)}.${d.slice(5, 7)}`),
    ),
  );
  if (both.length)
    out.push({
      key: 'absence-and-work',
      step: 'kontroller',
      title: T('{n, plural, one {# dag} other {# dager}} med både betalt fravær og godkjent vakt', {
        n: both.length,
      }),
      detail: T(
        'Dagen betales både som fravær og som arbeid: {days}. Avslå eller endre fraværet, eller rett vakten i Timeliste, hvis dagen ikke skal betales to ganger.',
        {
          days: `${both.slice(0, 6).join(', ')}${both.length > 6 ? ` ${T('og {n} til', { n: both.length - 6 })}` : ''}`,
        },
      ),
      count: both.length,
      severity: 'note',
      to: '/timeliste',
      records: rows.flatMap((r) =>
        [...new Set(Object.values(r.totals ?? {}).flatMap((l) => l.worked_dates ?? []))].map((d) => {
          const day = d.slice(0, 10);
          const s = (input.shifts ?? []).find(
            (x) => String(x.user_id) === String(r.id) && x.start_time.slice(0, 10) === day && x.id != null,
          );
          return {
            key: `w${r.id}:${day}`,
            who: r.name,
            date: day,
            to: s?.id != null ? shiftAt(r.id, s.id) : `/timeliste?ansatt=${r.id}&vis=godkjent`,
          };
        }),
      ),
    });

  const unpunched = sum((l) => l.unpunched);
  if (unpunched)
    out.push({
      key: 'unpunched',
      step: 'kontroller',
      title: T('{n, plural, one {# planlagt vakt} other {# planlagte vakter}} uten stempling', {
        n: unpunched,
      }),
      detail: 'De teller som null timer. Det er riktig hvis vakten ikke ble jobbet.',
      count: unpunched,
      severity: 'note',
      to: '/timeliste?vis=uten',
      records: dueOf('unpunched', 'begge'),
    });

  // #217: clock times on the wrong day and double clock-ins reached pay as overtime (one store, September and
  // October 2026). Two signs, on the approved clocked shifts of the period, each holding «Send» until looked at
  // (Lonn.tsx HOLD_UNTIL_SEEN): a shift over 13 h, past what AML § 10-5 allows even with an agreement, and a day
  // whose shifts add up past 9 h, so pay counts overtime there, with no single shift over 9 h
  const inPeriod = (d: string) => !input.period || (d >= input.period.from && d <= input.period.to);
  const worked = (input.shifts ?? []).filter(
    (s): s is typeof s & { id: number; user_id: number; hours: number } =>
      !!s.approved &&
      s.hours != null &&
      s.id != null &&
      s.user_id != null &&
      inPeriod(s.start_time.slice(0, 10)),
  );
  const nameOf = new Map(users.map((u) => [String(u.id), nameOfUser(u)]));
  const hrs = (h: number) => `${Math.round(h * 10) / 10} t`.replace('.', ',');
  const long = worked.filter((s) => s.hours > 13);
  if (long.length)
    out.push({
      key: 'long-shift',
      step: 'kontroller',
      title: T('{n, plural, one {# vakt er over 13 timer} other {# vakter er over 13 timer}}', {
        n: long.length,
      }),
      detail: T(
        'Arbeidsmiljøloven § 10-5 tillater høyst 13 timer, også med avtale. Så lange vakter er ofte en dobbel innstempling eller en utstempling som mangler. Se over før lønnen sendes.',
      ),
      count: long.length,
      severity: 'blocker',
      to: '/timeliste',
      records: long.map((s) => ({
        key: `s${s.id}`,
        who: nameOf.get(String(s.user_id)) ?? '',
        date: s.start_time.slice(0, 10),
        what: hrs(s.hours),
        to: shiftAt(s.user_id, s.id),
      })),
    });
  const days = new Map<string, typeof worked>();
  for (const s of worked) {
    const k = `${s.user_id}|${s.start_time.slice(0, 10)}`;
    days.set(k, [...(days.get(k) ?? []), s]);
  }
  const summed = [...days.values()].filter(
    (l) => l.reduce((a, s) => a + s.hours, 0) > 9 && l.every((s) => s.hours <= 9),
  );
  if (summed.length)
    out.push({
      key: 'overtime-split-day',
      step: 'kontroller',
      title: T(
        '{n, plural, one {# dag har overtid uten en vakt over 9 timer} other {# dager har overtid uten en vakt over 9 timer}}',
        { n: summed.length },
      ),
      detail: T(
        'Vaktene den dagen er til sammen over 9 timer, så lønnen regner overtid. Står stemplinger fra andre dager på samme dato, blir de betalt som overtid. Se over før lønnen sendes.',
      ),
      count: summed.length,
      severity: 'blocker',
      to: '/timeliste',
      records: summed.map((l) => {
        const s = l[0] as (typeof worked)[number];
        return {
          key: `d${s.user_id}:${s.start_time.slice(0, 10)}`,
          who: nameOf.get(String(s.user_id)) ?? '',
          date: s.start_time.slice(0, 10),
          what: T('{n} vakter, {h}', { n: l.length, h: hrs(l.reduce((a, x) => a + x.hours, 0)) }),
          to: shiftAt(s.user_id, s.id),
        };
      }),
    });

  return out;
}

export interface Step {
  key: StepKey;
  n: number;
  title: string;
  /** done: nothing blocking left here. now: the first step with something in it. todo: later. */
  state: 'done' | 'now' | 'todo';
  blockers: number;
  notes: number;
}

const TITLES: [StepKey, string][] = [
  ['kontroller', 'Kontroller'],
  ['godkjenn', 'Godkjenn timer'],
  ['eksporter', 'Eksporter'],
];

export function steps(list: Check[]): Step[] {
  const out = TITLES.map(([key, title], i) => {
    const mine = list.filter((c) => c.step === key);
    return {
      key,
      n: i + 1,
      title,
      state: 'todo' as Step['state'],
      blockers: mine.filter((c) => c.severity === 'blocker').length,
      notes: mine.filter((c) => c.severity === 'note').length,
    };
  });
  // The first step still holding a blocker is where the person is; everything before it is
  // done. Export is never "done", because nothing here knows whether the file was sent.
  const at = out.findIndex((s) => s.blockers > 0);
  for (const [i, s] of out.entries()) {
    if (at === -1) s.state = s.key === 'eksporter' ? 'now' : 'done';
    else if (i < at) s.state = 'done';
    else if (i === at) s.state = 'now';
  }
  return out;
}

/** Everything that will be wrong in the file if it is downloaded now. */
export const blockers = (list: Check[]): Check[] => list.filter((c) => c.severity === 'blocker');

/**
 * Whether the month may be called ready (#262): only once every source the checks read has answered for
 * this month and scope. A source on its way is pending, a refused one is an error named by what it is,
 * one being read again after a change is stale (the figures shown are the last answer for this same
 * month), and only then do the checks decide between needs review, ready and empty. A source a role
 * never asks for (the query is idle) is not waited on.
 */
export type Readiness = 'pending' | 'error' | 'stale' | 'empty' | 'review' | 'ready';
export interface Source {
  what: string;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  fetchStatus: string;
}
export function readiness(
  sources: Source[],
  checks: number,
  people: number,
): { state: Readiness; failed: string[] } {
  const failed = sources.filter((s) => s.isError).map((s) => s.what);
  if (failed.length) return { state: 'error', failed };
  if (sources.some((s) => s.isPending && s.fetchStatus !== 'idle')) return { state: 'pending', failed };
  if (sources.some((s) => s.isFetching)) return { state: 'stale', failed };
  return { state: checks ? 'review' : people ? 'ready' : 'empty', failed };
}
/** The states in which the checks and the figures are this month's own answer. */
export const settled = (r: Readiness) => r === 'review' || r === 'ready' || r === 'empty';
